import type { BootstrapData, DailyReport, DeviceSubscription, NotificationLog, Roadmap } from "./types";

const now = new Date();
const iso = (hour: number, minute = 0) => {
  const value = new Date(now);
  value.setHours(hour, minute, 0, 0);
  return value.toISOString();
};

export const demoReport: DailyReport = {
  reportDate: new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Tokyo" }).format(now),
  title: "朝の詳しいレポート",
  sourceLabel: "デモデータ",
  updatedAt: now.toISOString(),
  status: "complete",
  generatedAt: now.toISOString(),
  referenceUrls: [],
  content: `## 今日の見通し\n\n予定とニュースをまとめて確認できます。実際のレポートを貼り付けると、ここに全文が保存されます。\n\n## 今日の優先順位\n\n1. 午前中に重要な作業を一つ進める\n2. 予定の前後に余白を取る\n3. ニュースは元記事を開いて確認する\n\n## AI・IT ニュース\n\n### 記事ごとの要点\n\n- 概要、重要度の理由、出典リンクを含む長文レポートをそのまま表示できます。\n- 数値や固有名詞は、確認できた出典がある内容だけを記録します。\n\n## 今日の考察\n\n元の通知の文章量や構成を省略せず、日付別の履歴として残します。`,
};

export const demoBootstrap: BootstrapData = {
  authenticated: true,
  userEmail: "demo@example.com",
  date: new Intl.DateTimeFormat("ja-JP", { dateStyle: "full" }).format(now),
  weather: {
    condition: "晴れ時々くもり",
    temperatureMax: 28,
    temperatureMin: 21,
    precipitationProbability: 20,
    sourceName: "デモデータ",
    updatedAt: now.toISOString(),
  },
  trivia: {
    title: "金星では、1日が1年より長い",
    fact: "金星は自転が非常にゆっくりで、1回転に約243地球日、太陽を1周するのに約225地球日かかります。",
    share: "「金星は“1日”のほうが“1年”より長いんだって」と話せます。",
    sourceName: "NASA",
    sourceUrl: "https://science.nasa.gov/venus/facts/",
  },
  events: [
    { id: "e0", title: "資料提出日", start: iso(0), end: iso(23, 59), allDay: true, calendarName: "仕事" },
    { id: "e1", title: "週次ミーティング", start: iso(9, 30), end: iso(10, 15), allDay: false, location: "オンライン", calendarName: "仕事" },
    { id: "e2", title: "歯科検診", start: iso(15), end: iso(16), allDay: false, location: "下松市内", calendarName: "個人" },
  ],
  calendarStatus: "ok",
  topNews: {
    politics: [
      { id: "p1", category: "politics", title: "政策に関する公式発表の見出しがここに入ります", summary: "公式発表の要点を、短い日本語で確認できます。", importance: 4, importanceReason: "生活への影響範囲が広く、公式情報で確認できるため重要です。", evaluationMethod: "rules", sourceName: "デモ情報源", articleUrl: "https://example.com", publishedAt: iso(6, 15), fetchedAt: now.toISOString() },
      { id: "p2", category: "politics", title: "国内の主要な動きをまとめたニュース", summary: "同じ出来事の記事はまとめ、一次情報を優先して表示します。", importance: 3, importanceReason: "広く知っておく価値がある情報です。", evaluationMethod: "rules", sourceName: "デモ情報源", articleUrl: "https://example.com", publishedAt: iso(5, 40), fetchedAt: now.toISOString() },
    ],
    markets: [
      { id: "m1", category: "markets", title: "日米市場に影響する金融政策の発表", summary: "金利や為替、市場全体への影響を簡潔に整理します。", importance: 5, importanceReason: "日米の市場全体へ大きく影響する可能性があります。", evaluationMethod: "rules", sourceName: "デモ情報源", articleUrl: "https://example.com", publishedAt: iso(6), fetchedAt: now.toISOString() },
      { id: "m2", category: "markets", title: "主要な経済指標が公表されました", summary: "数値を創作せず、公開された情報と出典を示します。", importance: 3, importanceReason: "景気動向を把握する材料になるためです。", evaluationMethod: "rules", sourceName: "デモ情報源", articleUrl: "https://example.com", publishedAt: iso(4, 30), fetchedAt: now.toISOString() },
    ],
    "ai-it": [
      { id: "a1", category: "ai-it", title: "主要AIサービスに新しい開発機能", summary: "Web開発で使える変更点と注意点を日本語でまとめます。", importance: 4, importanceReason: "日常の開発フローへ直接影響する新機能です。", evaluationMethod: "rules", sourceName: "デモ情報源", articleUrl: "https://example.com", publishedAt: iso(6, 30), fetchedAt: now.toISOString() },
      { id: "a2", category: "ai-it", title: "クラウド基盤の仕様変更が発表", summary: "影響範囲と確認すべき期限を短く整理します。", importance: 3, importanceReason: "Webアプリの運用に関係する仕様変更です。", evaluationMethod: "rules", sourceName: "デモ情報源", articleUrl: "https://example.com", publishedAt: iso(3, 45), fetchedAt: now.toISOString() },
    ],
    qiita: [
      { id: "q1", category: "qiita", title: "Cloudflare WorkersとD1で個人用ダッシュボードを作る", summary: "cronでRSSを集め、D1に保存し、React PWAで表示するまでの構成と詰まりどころをまとめます。", importance: 4, importanceReason: "いいね86・ストック52。タグ: Cloudflare, React, TypeScript。多くの読者に評価されている記事です。", evaluationMethod: "rules", sourceName: "Qiita", articleUrl: "https://qiita.com/example/items/1", publishedAt: iso(5, 20), fetchedAt: now.toISOString(), likesCount: 86, stocksCount: 52, tags: ["Cloudflare", "React", "TypeScript"] },
      { id: "q2", category: "qiita", title: "React 19のuse()とSuspenseの実践パターン", summary: "データ取得をuse()に寄せたときの再レンダリングとエラー境界の扱いを、小さなサンプルで確認します。", importance: 3, importanceReason: "いいね14・ストック9。タグ: React, JavaScript。一定の反応がある記事です。", evaluationMethod: "rules", sourceName: "Qiita", articleUrl: "https://qiita.com/example/items/2", publishedAt: iso(2, 10), fetchedAt: now.toISOString(), likesCount: 14, stocksCount: 9, tags: ["React", "JavaScript"] },
    ],
  },
  lastNewsCheckedAt: now.toISOString(),
  failures: [],
  sourceStatuses: [{ source: "デモ情報源", status: "ok", lastSuccessAt: now.toISOString(), lastAttemptAt: now.toISOString() }],
  settings: { morningTime: "07:00", politicsEnabled: true, marketsEnabled: true, aiItEnabled: true, qiitaEnabled: true, breakingEnabled: true, disasterEnabled: true, nonDisasterBreakingLimit: 3, theme: "system", quietStart: "22:00", quietEnd: "07:00", notificationsPausedUntil: null, digestEnabled: false },
  todayReport: demoReport,
  demo: true,
};

export const demoHistory: NotificationLog[] = [
  { id: "n1", kind: "morning_schedule", title: "今日の予定と下松市の天気", sentAt: iso(7), status: "sent" },
  { id: "n2", kind: "morning_ai-it", title: "AI・ITニュース 上位3件", sentAt: iso(7), status: "sent" },
];

export const demoDevices: DeviceSubscription[] = [
  { id: "device-demo", deviceName: "このChrome", createdAt: now.toISOString(), lastSuccessAt: now.toISOString(), active: true, currentDevice: true },
];

// A short slice of the real sheet layout so the roadmap screens render in Vite without the Sheets API.
const demoWeekStart = (() => { const monday = new Date(now); monday.setDate(now.getDate() - ((now.getDay() + 6) % 7)); return monday; })();
const demoDate = (offsetWeeks: number) => new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Tokyo" }).format(new Date(demoWeekStart.getTime() + offsetWeeks * 7 * 86_400_000));
const demoMonth = (offsetWeeks: number) => demoDate(offsetWeeks).slice(0, 7).replace("-", "/");
export const demoRoadmap: Roadmap = {
  title: "Webエンジニア学習ロードマップ（デモ）",
  notes: ["デモデータです。実際のシートの内容ではありません。"],
  currentRank: "",
  statusOptions: ["未着手", "進行中", "完了"],
  months: [
    { month: demoMonth(-1), stage: "準備・現状把握・HTML/CSS", goal: "現状を把握し、説明できない箇所を洗い出す。", checkpoint: "機能一覧と説明できない箇所リストがある。", jobPrep: "業務を棚卸しする。", plannedHours: 23, weekCount: 3, completedWeeks: 1, rankTarget: "", achievedDate: "" },
    { month: demoMonth(4), stage: "JavaScript基礎・DOM", goal: "JavaScriptだけで小アプリを作る。", checkpoint: "追加・削除・絞り込みが自力でできる。", jobPrep: "学習記録を毎週残す。", plannedHours: 40, weekCount: 4, completedWeeks: 0, rankTarget: "Dランク", achievedDate: "" },
  ],
  weeks: [
    { row: 8, startDate: demoDate(-1), month: demoMonth(-1), goal: "READMEとフォルダ構成を読み、画面と機能を一覧化する。", deliverable: "機能一覧と『説明できない箇所リスト』をリポジトリに置く。", materialIds: "R09 / R23", plannedHours: 10, status: "完了", actualHours: 9.5, evidence: "https://github.com/example/pr/1", weekType: "学習" },
    { row: 9, startDate: demoDate(0), month: demoMonth(0), goal: "HTMLの見出し・表・フォーム、CSSの余白・Flexbox・レスポンシブ。", deliverable: "同じ入力項目の静的フォームを自分で書く。幅375pxで崩れないことを確認する。", materialIds: "R01", plannedHours: 10, status: "進行中", actualHours: 3, evidence: "", weekType: "学習" },
    { row: 10, startDate: demoDate(1), month: demoMonth(1), goal: "予備週：積み残しの回収と復習。", deliverable: "説明できるようになったものに印を付ける。", materialIds: "当月の教材", plannedHours: 3, status: "未着手", actualHours: null, evidence: "", weekType: "予備" },
    { row: 11, startDate: demoDate(2), month: demoMonth(2), goal: "const/let、型、条件分岐、配列、関数。", deliverable: "日別件数を出す関数と入力例を作る。", materialIds: "R02 / R03", plannedHours: 10, status: "未着手", actualHours: null, evidence: "", weekType: "学習" },
  ],
  weeklySheetTitle: "週別進捗",
  spreadsheetUrl: "https://docs.google.com/spreadsheets/",
  fetchedAt: now.toISOString(),
};
